import { TLMSProgram } from '../../../types'
import {
  CONFIGURATION_GROUP_KEY,
  ConfigurationTreeKey,
  ConfigurationType,
  ProgramImportConfigurationRequest,
} from './types'

export const CONFIGURATION_TYPE_ORDER: ConfigurationType[] = [
  'PROGRAM_CONFIGURATION_BLUEPRINT_REGISTRATION',
  'PROGRAM_CONFIGURATION_AUTO_APPROVE_DIKLAT_PARTICIPANTS',
  'PROGRAM_CONFIGURATION_HIDE_ADD_PARTICIPANT',
  'PROGRAM_CONFIGURATION_MODIFY_PROFILE',
  'PROGRAM_CONFIGURATION_AUTO_APPROVE_MODIFY_PROFILE',
  'PROGRAM_CONFIGURATION_GRADE_SETTINGS',
  'PROGRAM_CONFIGURATION_SELF_ENROLLMENT',
  'PROGRAM_CONFIGURATION_UMPAN_BALIK',
  'PROGRAM_CONFIGURATION_CERTIFICATE_DESIGN_TEMPLATES',
  'PROGRAM_CONFIGURATION_CERTIFICATE_PREREQUISITES',
]

export const PARTICIPANT_CONFIGURATION_TYPES: ConfigurationType[] = [
  'PROGRAM_CONFIGURATION_AUTO_APPROVE_DIKLAT_PARTICIPANTS',
  'PROGRAM_CONFIGURATION_HIDE_ADD_PARTICIPANT',
  'PROGRAM_CONFIGURATION_MODIFY_PROFILE',
  'PROGRAM_CONFIGURATION_AUTO_APPROVE_MODIFY_PROFILE',
]

export const CERTIFICATE_CONFIGURATION_TYPES: ConfigurationType[] = [
  'PROGRAM_CONFIGURATION_CERTIFICATE_DESIGN_TEMPLATES',
  'PROGRAM_CONFIGURATION_CERTIFICATE_PREREQUISITES',
]

const GROUP_CONFIGURATION_TYPES = {
  [CONFIGURATION_GROUP_KEY.PARTICIPANT_MANAGEMENT]:
    PARTICIPANT_CONFIGURATION_TYPES,
  [CONFIGURATION_GROUP_KEY.CERTIFICATE]: CERTIFICATE_CONFIGURATION_TYPES,
}

const PROFILE_CONFIGURATION = 'PROGRAM_CONFIGURATION_MODIFY_PROFILE'
const PROFILE_AUTO_APPROVE_CONFIGURATION =
  'PROGRAM_CONFIGURATION_AUTO_APPROVE_MODIFY_PROFILE'

const isConfigurationGroupKey = (
  key: ConfigurationTreeKey,
): key is keyof typeof GROUP_CONFIGURATION_TYPES =>
  key in GROUP_CONFIGURATION_TYPES

export const normalizeConfigurationTypes = (
  values: ConfigurationType[],
): ConfigurationType[] => {
  const selectedValues = new Set(values)

  return CONFIGURATION_TYPE_ORDER.filter((value) => selectedValues.has(value))
}

export const updateConfigurationSelection = (
  currentValues: ConfigurationType[],
  key: ConfigurationTreeKey,
  checked: boolean,
): ConfigurationType[] => {
  const nextValues = new Set(currentValues)
  const updateValue = (value: ConfigurationType) => {
    if (checked) {
      nextValues.add(value)
      return
    }

    nextValues.delete(value)
  }

  if (isConfigurationGroupKey(key)) {
    GROUP_CONFIGURATION_TYPES[key].forEach(updateValue)
    return normalizeConfigurationTypes([...nextValues])
  }

  if (key === PROFILE_CONFIGURATION) {
    updateValue(PROFILE_CONFIGURATION)
    updateValue(PROFILE_AUTO_APPROVE_CONFIGURATION)
    return normalizeConfigurationTypes([...nextValues])
  }

  if (key === PROFILE_AUTO_APPROVE_CONFIGURATION && checked) {
    nextValues.add(PROFILE_CONFIGURATION)
  }

  updateValue(key)
  return normalizeConfigurationTypes([...nextValues])
}

export const getConfigurationTreeState = (
  selectedValues: ConfigurationType[],
): {
  checked: ConfigurationTreeKey[]
  halfChecked: ConfigurationTreeKey[]
} => {
  const selectedSet = new Set(selectedValues)
  const checked: ConfigurationTreeKey[] = [...selectedValues]
  const halfChecked: ConfigurationTreeKey[] = []

  Object.entries(GROUP_CONFIGURATION_TYPES).forEach(([groupKey, values]) => {
    const selectedCount = values.filter((value) =>
      selectedSet.has(value),
    ).length

    if (selectedCount === values.length) {
      checked.push(groupKey as ConfigurationTreeKey)
      return
    }

    if (selectedCount > 0) {
      halfChecked.push(groupKey as ConfigurationTreeKey)
    }
  })

  return { checked, halfChecked }
}

export const getSourceProgramOptions = (
  programs: TLMSProgram[],
  currentProgramId: string | number,
): {
  label: string
  value: number
  disabled: boolean
  status: TLMSProgram['status']
}[] =>
  programs
    .filter(({ id }) => String(id) !== String(currentProgramId))
    .map(({ id, name, status }) => ({
      label: name,
      value: id,
      disabled: String(id) === String(currentProgramId),
      status,
    }))

export const buildProgramImportConfigurationRequest = (
  sourceProgramId: string | number,
  selectedValues: ConfigurationType[],
): ProgramImportConfigurationRequest => ({
  sourceProgramId: String(sourceProgramId),
  listConfigurationType: normalizeConfigurationTypes(selectedValues),
})
